/**
 * Luemas homepage section navigation.
 *
 * - Training Classes automatically routes to the Luemas Training section.
 * - Other known menu labels automatically route to their matching homepage section.
 * - Any new Shopify navigation item can target a section with /#section-name.
 * - Event delegation means newly added menu links inherit the behaviour automatically.
 */
(() => {
  const HEADER_LINK_SELECTOR = '#header-group a[href], header-component a[href], .header__drawer a[href]';
  const TARGET_SELECTOR = '[data-luemas-anchor]';

  const KNOWN_ALIASES = new Map([
    ['home', 'home'], ['homepage', 'home'], ['hero', 'home'],
    ['services', 'services'], ['luxury services', 'services'], ['book a service', 'services'], ['salon services', 'services'],
    ['our signature services', 'signature-services'], ['signature services', 'signature-services'],
    ['our values', 'our-values'], ['values', 'our-values'], ['what we stand for', 'our-values'],
    ['gallery', 'gallery'], ['lookbook', 'gallery'], ['transformations', 'gallery'], ['hair transformations', 'gallery'],
    ['about', 'about'], ['about us', 'about'], ['our artistry', 'about'], ['artistry', 'about'],
    ['scalp therapy', 'scalp-therapy'], ['scalp care', 'scalp-therapy'], ['hair treatments', 'scalp-therapy'],
    ['aftercare', 'aftercare'], ['shop aftercare', 'aftercare'], ['aftercare essentials', 'aftercare'], ['hair care products', 'aftercare'],
    ['memberships', 'memberships'], ['membership', 'memberships'], ['monthly subscription plans', 'memberships'], ['subscription plans', 'memberships'],
    ['training classes', 'luemas-training'], ['training', 'luemas-training'], ['luemas training', 'luemas-training'], ['hair training', 'luemas-training'],
    ['reviews', 'reviews'], ['client reviews', 'reviews'], ['testimonials', 'reviews'], ['client love', 'reviews'],
    ['faq', 'faq'], ['faqs', 'faq'], ['frequently asked questions', 'faq'],
    ['book appointment', 'booking'], ['booking', 'booking'], ['book now', 'booking'], ['contact', 'booking'], ['contact us', 'booking'],
    ['footer', 'footer'], ['opening hours', 'footer'], ['social media', 'footer']
  ]);

  let targetIndex = new Map();

  const normalize = (value = '') => value
    .toString()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[’']/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const slugify = (value = '') => normalize(value).replace(/\s+/g, '-');

  const getHeader = () => document.querySelector('#header-component');
  const isEnabled = () => getHeader()?.dataset.luemasSectionNavigation !== 'false';
  const shouldAutoMatch = () => getHeader()?.dataset.luemasAutoMatchSections !== 'false';
  const getBehavior = () => getHeader()?.dataset.luemasScrollBehavior === 'instant' ? 'auto' : 'smooth';
  const getExtraOffset = () => Number.parseInt(getHeader()?.dataset.luemasScrollOffset || '12', 10) || 0;

  const addIndexEntry = (key, target) => {
    const normalized = normalize(key);
    if (!normalized || !(target instanceof HTMLElement) || targetIndex.has(normalized)) return;
    targetIndex.set(normalized, target);
    targetIndex.set(slugify(normalized), target);
  };

  const rebuildTargetIndex = () => {
    targetIndex = new Map();

    document.querySelectorAll(TARGET_SELECTOR).forEach((target) => {
      if (!(target instanceof HTMLElement)) return;

      const canonical = target.dataset.luemasAnchor || '';
      if (canonical && !target.id && !document.getElementById(canonical)) {
        target.id = canonical;
      }

      addIndexEntry(canonical, target);

      const aliases = (target.dataset.luemasMenuAliases || '').split('|');
      aliases.forEach((alias) => addIndexEntry(alias, target));

      if (shouldAutoMatch()) {
        const wrapper = target.closest('.shopify-section') || target;
        wrapper.querySelectorAll('h1, h2, h3, [aria-label]').forEach((node) => {
          const label = node.getAttribute('aria-label') || node.textContent || '';
          addIndexEntry(label, target);
        });
      }
    });
  };

  const knownSlugFor = (value) => KNOWN_ALIASES.get(normalize(value)) || '';

  const resolveTarget = (token) => {
    if (!token) return null;
    let decoded = token;
    try { decoded = decodeURIComponent(token); } catch (_) {}

    const normalized = normalize(decoded.replace(/^#/, ''));
    const slug = knownSlugFor(normalized) || slugify(normalized);

    return targetIndex.get(normalized)
      || targetIndex.get(slug)
      || document.getElementById(decoded.replace(/^#/, ''))
      || document.getElementById(slug)
      || null;
  };

  const rootPath = () => {
    const configured = window.LuemasSectionNavigation?.rootUrl;
    if (typeof configured === 'string' && configured) return configured;
    return '/';
  };

  const onHomePage = () => Boolean(document.querySelector('#MainContent [data-luemas-anchor]'));

  const getExplicitToken = (link) => {
    const rawHref = link.getAttribute('href') || '';
    if (rawHref.startsWith('#')) return rawHref.slice(1);

    let url;
    try { url = new URL(link.href, window.location.href); } catch (_) { return ''; }
    if (url.origin !== window.location.origin) return '';

    const queryToken = url.searchParams.get('luemas-section') || url.searchParams.get('section-anchor');
    if (queryToken) return queryToken;
    if (url.hash) return url.hash.slice(1);
    return '';
  };

  const getAutomaticToken = (link) => {
    const label = (link.textContent || link.getAttribute('aria-label') || '').trim();
    const known = knownSlugFor(label);
    if (known) return known;
    if (shouldAutoMatch() && resolveTarget(label)) return label;
    return '';
  };

  const closeMobileNavigation = () => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true }));
    document.querySelectorAll('#header-group details[open]').forEach((details) => details.removeAttribute('open'));
    document.querySelectorAll('#header-group dialog[open]').forEach((dialog) => {
      if (typeof dialog.close === 'function') dialog.close();
    });
  };

  const markActiveLink = (activeLink, token) => {
    document.querySelectorAll(HEADER_LINK_SELECTOR).forEach((link) => {
      link.classList.remove('luemas-section-link--active');
      link.removeAttribute('data-luemas-section-active');
    });
    if (activeLink) {
      activeLink.classList.add('luemas-section-link--active');
      activeLink.setAttribute('data-luemas-section-active', token);
    }
  };

  const scrollToTarget = (target, token, link = null, updateHistory = true, behaviorOverride = '') => {
    if (!(target instanceof HTMLElement)) return false;

    const headerGroup = document.querySelector('#header-group');
    const headerHeight = headerGroup instanceof HTMLElement ? headerGroup.getBoundingClientRect().height : 0;
    const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerHeight - getExtraOffset());
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = behaviorOverride || (reducedMotion ? 'auto' : getBehavior());

    closeMobileNavigation();
    window.scrollTo({ top, behavior });

    if (updateHistory && token) {
      const hash = `#${slugify(token)}`;
      if (window.location.hash !== hash) history.pushState(null, '', hash);
    }

    markActiveLink(link, token);
    return true;
  };

  const handleMenuClick = (event) => {
    if (!isEnabled() || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest(HEADER_LINK_SELECTOR);
    if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('download') || link.target === '_blank') return;

    const explicitToken = getExplicitToken(link);
    const automaticToken = explicitToken || getAutomaticToken(link);
    if (!automaticToken) return;

    rebuildTargetIndex();
    const target = resolveTarget(automaticToken);

    if (target && onHomePage()) {
      event.preventDefault();
      scrollToTarget(target, target.dataset.luemasAnchor || automaticToken, link, true);
      return;
    }

    const known = knownSlugFor(automaticToken) || slugify(automaticToken);
    if (known) {
      event.preventDefault();
      window.location.assign(`${rootPath().replace(/\/$/, '') || ''}/#${known}`);
    }
  };

  const handleInitialHash = () => {
    if (!isEnabled() || !window.location.hash) return;
    rebuildTargetIndex();
    const token = window.location.hash.slice(1);
    const target = resolveTarget(token);
    if (!target) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => scrollToTarget(target, target.dataset.luemasAnchor || token, null, false, 'auto'));
    });
  };

  const init = () => {
    rebuildTargetIndex();
    handleInitialHash();
  };

  document.addEventListener('click', handleMenuClick);
  document.addEventListener('DOMContentLoaded', init, { once: true });
  window.addEventListener('pageshow', handleInitialHash);
  window.addEventListener('hashchange', handleInitialHash);
  document.addEventListener('shopify:section:load', rebuildTargetIndex);
  document.addEventListener('shopify:section:reorder', rebuildTargetIndex);
})();
